// Self-initializing analytics loader (GTM + Microsoft Clarity)
// Defers loading until user interaction or timeout to reduce TBT
//
// Why this approach:
// - No DOM elements needed (avoids invalid <div> in <head>)
// - Idempotent (safe across Turbo navigations)
// - Self-initializing (runs on import)
// - Production only (excludes localhost, *.test, stage.*)
//
// Note: GA4 is loaded via GTM ("PRA Master Tag 2"), not directly here.
// This ensures single GA4 instance and proper Conversion Linker integration.

// Configuration
const GTM_ID = "GTM-KVJZQNCT"
const CLARITY_ID = "xey3gq1ryg"
const DELAY_MS = 3000
const INTERACTION_EVENTS = ["scroll", "click", "touchstart", "keydown", "mousemove"]

// URLs
const GTM_SCRIPT_URL = "https://www.googletagmanager.com/gtm.js"
const GTM_SCRIPT_SELECTOR = 'script[src*="googletagmanager.com/gtm.js"]'
const CLARITY_SCRIPT_URL = "https://www.clarity.ms/tag"
const CLARITY_SCRIPT_SELECTOR = 'script[src*="clarity.ms/tag"]'

// Non-production hostnames
const DEV_HOSTNAMES = ["localhost", "127.0.0.1", ".test"]
const STAGING_PREFIX = "stage."

let loaded = false
let timeout = null

function isProduction() {
  const hostname = window.location.hostname
  const isDev = DEV_HOSTNAMES.some(dev => hostname.includes(dev))
  const isStaging = hostname.startsWith(STAGING_PREFIX)
  return !isDev && !isStaging
}

function scriptsAlreadyInjected() {
  return document.querySelector(GTM_SCRIPT_SELECTOR) !== null
}

function createScript(src) {
  const script = document.createElement("script")
  script.async = true
  script.src = src
  document.head.appendChild(script)
}

function injectGTM() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" })
  createScript(`${GTM_SCRIPT_URL}?id=${GTM_ID}`)
}

function injectClarity() {
  if (document.querySelector(CLARITY_SCRIPT_SELECTOR)) return
  // Queue calls until the tag script defines the real clarity() (per MS snippet)
  window.clarity = window.clarity || function () {
    (window.clarity.q = window.clarity.q || []).push(arguments)
  }
  createScript(`${CLARITY_SCRIPT_URL}/${CLARITY_ID}`)
}

function cleanup() {
  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }
  INTERACTION_EVENTS.forEach(event => {
    document.removeEventListener(event, loadAnalytics)
  })
}

function loadAnalytics() {
  if (loaded) return
  if (!isProduction()) return
  if (scriptsAlreadyInjected()) {
    loaded = true
    return
  }

  loaded = true
  cleanup()
  injectGTM()
  injectClarity()
}

function init() {
  // Don't initialize if not in production
  if (!isProduction()) return

  // Don't re-initialize if already loaded
  if (scriptsAlreadyInjected()) {
    loaded = true
    return
  }

  // Bind interaction events (load on first interaction)
  INTERACTION_EVENTS.forEach(event => {
    document.addEventListener(event, loadAnalytics, { once: true, passive: true })
  })

  // Fallback timeout (load after delay even without interaction)
  timeout = setTimeout(loadAnalytics, DELAY_MS)
}

// Self-initialize on import
init()

export { loadAnalytics }
